import React from 'react';
import { Copy } from 'lucide-react';

import { Button } from './ui/button';
import { DialogDescription, DialogTitle } from './ui/dialog';
import { copyText } from '../lib/clipboard';

interface RecordDialogHeaderProps {
  /** The record's name, as saved (never the live form value). */
  title: React.ReactNode;
  /** StatusBadges beside the title. */
  badges?: React.ReactNode;
  /** The record's identifier (slug, client ID, UUID), shown in mono as the description. */
  id: string;
  /**
   * A few words read before the id, so the description is "Client ID 4f9c…"
   * rather than a bare string of characters. Screen-reader only.
   */
  srDescription: string;
  /**
   * Names the id for a copy button beside it ('Sensor ID'): its accessible
   * name is "Copy {copyLabel}" and the toast "{copyLabel} copied". Absent, the
   * id is plain text — pass it where the id is not also in a CredentialField.
   */
  copyLabel?: string;
  /**
   * Facts that must stay in view whichever section is open (the owning
   * organization, Created, Last seen). A small wrapped list under the id.
   */
  facts?: ReadonlyArray<{ label: string; value: React.ReactNode }>;
}

/**
 * The fixed identity block of a record workbench (frame dialog): name and
 * badges on one line, the mono id beneath. Stays put while sections change.
 * The frame has no padding of its own, so this carries it — and `pr-14`
 * clears the close X.
 */
export const RecordDialogHeader: React.FC<RecordDialogHeaderProps> = ({
  title,
  badges,
  id,
  srDescription,
  copyLabel,
  facts,
}) => (
  <div className="shrink-0 border-b px-6 py-4 pr-14">
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
      <DialogTitle>{title}</DialogTitle>
      {badges}
    </div>
    <div className="mt-0.5 flex min-w-0 items-center gap-1">
      <DialogDescription className="min-w-0 break-all font-mono text-xs">
        <span className="sr-only">{srDescription} </span>
        {id}
      </DialogDescription>
      {copyLabel ? (
        // Outside the description, so the dialog's accessible description stays "{srDescription} {id}".
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          aria-label={`Copy ${copyLabel}`}
          onClick={() => void copyText(id, { label: copyLabel })}
        >
          <Copy aria-hidden="true" />
        </Button>
      ) : null}
    </div>
    {facts && facts.length > 0 ? (
      <dl className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs" data-record-facts="">
        {facts.map((fact) => (
          <div key={fact.label} className="flex min-w-0 gap-1.5">
            <dt className="text-muted-foreground">{fact.label}</dt>
            <dd className="min-w-0 break-words">{fact.value}</dd>
          </div>
        ))}
      </dl>
    ) : null}
  </div>
);
